import { useCallback, useEffect, useRef, useState, type FocusEvent as ReactFocusEvent } from 'react';
import {
  fileFromClipboard,
  fileFromClipboardEvent,
  isEditablePasteTarget,
} from './clipboardImage.js';

type PasteTarget = {
  accept?: string;
  onFile: (file: File) => void;
};

const pasteStack: PasteTarget[] = [];
let docBound = false;

function onDocumentPaste(event: ClipboardEvent) {
  if (isEditablePasteTarget(event.target)) return;
  const target = pasteStack[pasteStack.length - 1];
  if (!target) return;
  const file = fileFromClipboardEvent(event, target.accept);
  if (!file) return;
  event.preventDefault();
  target.onFile(file);
}

function ensureDocListener() {
  if (docBound || typeof document === 'undefined') return;
  docBound = true;
  document.addEventListener('paste', onDocumentPaste);
}

export function useClipboardImagePaste(opts: {
  disabled?: boolean;
  accept?: string;
  extraActive?: boolean;
  onFile: (file: File) => void;
}) {
  const { disabled = false, accept, extraActive = false, onFile } = opts;
  const [hot, setHot] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const onFileRef = useRef(onFile);
  onFileRef.current = onFile;

  const active = !disabled && (hot || extraActive);

  useEffect(() => {
    if (!active) return;
    const entry: PasteTarget = {
      accept,
      onFile: (file) => onFileRef.current(file),
    };
    pasteStack.push(entry);
    ensureDocListener();
    return () => {
      const i = pasteStack.lastIndexOf(entry);
      if (i >= 0) pasteStack.splice(i, 1);
    };
  }, [active, accept]);

  const pasteFromClipboard = useCallback(async () => {
    if (disabled) return false;
    const file = await fileFromClipboard(accept);
    if (!file) return false;
    onFileRef.current(file);
    return true;
  }, [accept, disabled]);

  const wrapProps = {
    ref: wrapRef,
    onPointerEnter: () => setHot(true),
    onPointerLeave: () => setHot(false),
    onFocusCapture: () => setHot(true),
    onBlurCapture: (event: ReactFocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node && wrapRef.current?.contains(next)) return;
      setHot(false);
    },
  };

  return { wrapProps, pasteFromClipboard, active };
}
